import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Send, MessageCircle } from "lucide-react";
import Initials from "../components/Initials";
import { useT } from "../i18n/I18nContext";

export default function Messages() {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const listRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data);
      if (!userId && data.length && !active) {
        setActive(data[0].user);
      }
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[messages] failed to load threads", err);
      }
    }
  }, [active, userId]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  useEffect(() => {
    if (!userId) return;
    api.get(`/users/${userId}`).then(({ data }) => setActive(data)).catch(() => {});
  }, [userId]);

  const loadMsgs = useCallback(async () => {
    if (!active) return;
    try {
      const { data } = await api.get(`/messages/${active.id}`);
      setMsgs(data);
      setTimeout(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
      }, 50);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [active]);

  useEffect(() => { loadMsgs(); }, [loadMsgs]);

  useEffect(() => {
    if (!active) return;
    const intv = setInterval(loadMsgs, 5000);
    return () => clearInterval(intv);
  }, [active, loadMsgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !active) return;
    try {
      await api.post("/messages", { recipient_id: active.id, body });
      setBody("");
      await loadMsgs();
      await loadThreads();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14 anim-fade-up" data-testid="messages-page">
      <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold mb-2">{t("messages.overline")}</div>
      <h1 className="text-3xl md:text-4xl font-semibold" style={{ fontFamily: "Outfit" }}>{t("messages.title")}</h1>

      <div className="mt-8 grid lg:grid-cols-[320px_1fr] gap-6">
        <aside className="card-mabw p-2 md:p-3 max-h-[640px] overflow-auto">
          {threads.length === 0 ? (
            <div className="p-6 text-sm text-mabw-soft text-center" data-testid="msg-empty-threads">
              {t("messages.empty_threads")}
            </div>
          ) : (
            <ul>
              {threads.map((th) => (
                <li key={th.user.id}>
                  <button
                    onClick={() => { setActive(th.user); navigate(`/app/messaggi/${th.user.id}`); }}
                    data-testid={`thread-${th.user.id}`}
                    className={`w-full text-left p-3 rounded-xl flex items-center gap-3 transition-colors ${
                      active?.id === th.user.id ? "bg-stone-100" : "hover:bg-stone-50"
                    }`}
                  >
                    <Initials name={`${th.user.first_name} ${th.user.last_initial}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium truncate">{th.user.first_name} {th.user.last_initial}</div>
                        {th.unread > 0 && (
                          <span className="text-[10px] bg-mabw-primary text-white rounded-full px-2 py-0.5">{th.unread}</span>
                        )}
                      </div>
                      <div className="text-xs text-mabw-soft truncate">{th.last_body}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="card-mabw flex flex-col h-[640px]">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-mabw-soft p-10 text-center" data-testid="msg-empty-state">
              <MessageCircle className="w-10 h-10 mb-3 text-mabw-secondary" />
              {t("messages.empty_state")}
            </div>
          ) : (
            <>
              <header className="p-4 border-b border-mabw flex items-center gap-3">
                <Initials name={`${active.first_name} ${active.last_initial}`} />
                <div>
                  <Link to={`/app/utenti/${active.id}`} className="font-semibold hover:text-mabw-primary" data-testid="msg-header-user">
                    {active.first_name} {active.last_initial}
                  </Link>
                  <div className="text-xs text-mabw-soft">{active.city}</div>
                </div>
              </header>
              <div ref={listRef} className="flex-1 overflow-auto p-4 space-y-3" data-testid="msg-list">
                {msgs.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                        mine ? "bg-mabw-primary text-white" : "bg-stone-100 text-mabw-text"
                      }`}>
                        {m.body}
                      </div>
                    </div>
                  );
                })}
                {msgs.length === 0 && <div className="text-center text-sm text-mabw-soft">{t("messages.no_messages")}</div>}
              </div>
              {error && <div className="px-4 pb-2 text-xs text-red-600">{error}</div>}
              <form onSubmit={send} className="p-3 border-t border-mabw flex gap-2">
                <input
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={t("messages.input_placeholder")}
                  className="input-mabw flex-1"
                  data-testid="msg-input"
                />
                <button type="submit" className="btn-mabw" data-testid="msg-send-btn">
                  <Send className="w-4 h-4" /> {t("messages.send")}
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
